import { useEffect, useRef, useState } from 'react';
import { ApiError, transcribeAudio } from '../lib/api.js';

/**
 * Mic button that records a clip via MediaRecorder, sends it to
 * /transcribe (Whisper), and hands the resulting text back via
 * `onTranscript`. The caller decides whether to replace or append the
 * text — we don't manage textarea state here.
 *
 * UI states:
 *   idle      → 🎤 「音声で答える」
 *   recording → ● 「録音を止める」 + elapsed timer
 *   uploading → ⏳ 「文字起こし中…」 (button disabled)
 *
 * Errors (mic permission denied, transcribe failed) bubble out via
 * `onError` so the parent can render them inline next to its existing
 * error block instead of toasting.
 *
 * Browser support: MediaRecorder is available everywhere we care about
 * (Chrome/Edge/Safari 14.1+/Firefox). On unsupported browsers the
 * button stays disabled with a tooltip.
 */

interface Props {
  onTranscript: (text: string) => void;
  onError?: (message: string) => void;
  /** Disable from the parent during structuring/committing. */
  disabled?: boolean;
  /** BCP-47 hint for Whisper. Defaults to 'ja'. */
  language?: string;
}

type RecorderState =
  | { kind: 'idle' }
  | { kind: 'recording'; startedAt: number }
  | { kind: 'uploading' };

const SUPPORTED_MIMES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of SUPPORTED_MIMES) {
    // isTypeSupported is lenient; the empty string fallback below covers
    // browsers that ignore the param entirely.
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  // btoa needs a binary string; chunk to avoid spread-arg call-stack limits.
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]);
  }
  return btoa(binary);
}

export function VoiceInputButton({ onTranscript, onError, disabled, language = 'ja' }: Props) {
  const [state, setState] = useState<RecorderState>({ kind: 'idle' });
  const [elapsedSec, setElapsedSec] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const supportedMime = pickMimeType();
  const supported = supportedMime !== null;

  // Tick the recording timer.
  useEffect(() => {
    if (state.kind !== 'recording') return;
    const startedAt = state.startedAt;
    const id = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, [state]);

  useEffect(() => {
    return () => {
      // Best-effort cleanup if the parent unmounts mid-recording.
      if (recorderRef.current && recorderRef.current.state === 'recording') {
        try {
          recorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = supportedMime ?? '';
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        // Free the mic immediately so the indicator goes away.
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (blob.size === 0) {
          setState({ kind: 'idle' });
          setElapsedSec(0);
          return;
        }
        setState({ kind: 'uploading' });
        try {
          const audio_base64 = await blobToBase64(blob);
          const r = await transcribeAudio({
            audio_base64,
            mime_type: blob.type || 'audio/webm',
            language,
          });
          onTranscript(r.text);
        } catch (err) {
          const message =
            err instanceof ApiError
              ? err.userMessage
              : err instanceof Error
                ? err.message
                : String(err);
          onError?.(message);
        } finally {
          setState({ kind: 'idle' });
          setElapsedSec(0);
        }
      };
      recorder.start();
      setState({ kind: 'recording', startedAt: Date.now() });
      setElapsedSec(0);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'マイクが使えません。ブラウザの権限を確認してください。';
      onError?.(message);
    }
  };

  const stopRecording = () => {
    const r = recorderRef.current;
    if (r && r.state === 'recording') {
      r.stop();
    }
  };

  if (!supported) {
    return (
      <button
        type="button"
        className="btn-ghost voice-btn"
        disabled
        title="このブラウザは音声入力に対応していません"
      >
        🎤 音声入力(非対応)
      </button>
    );
  }

  if (state.kind === 'recording') {
    const mm = String(Math.floor(elapsedSec / 60)).padStart(1, '0');
    const ss = String(elapsedSec % 60).padStart(2, '0');
    return (
      <button
        type="button"
        className="btn voice-btn voice-btn--recording"
        onClick={stopRecording}
      >
        <span className="voice-dot" /> 録音を止める ({mm}:{ss})
      </button>
    );
  }

  if (state.kind === 'uploading') {
    return (
      <button type="button" className="btn-ghost voice-btn" disabled>
        文字起こし中…
      </button>
    );
  }

  return (
    <button
      type="button"
      className="btn-ghost voice-btn"
      onClick={startRecording}
      disabled={disabled}
    >
      🎤 音声で答える
    </button>
  );
}
